import React from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import NavRow from '../fields/NavRow';
import TextField from '../fields/TextField';
import { TaskDefinition, TaskStepRenderArgs } from '../QuestionnaireShell';
import { NameValues } from '../types';
import { zodResolver } from '../validation';

const NAME_PATTERN = /^[A-Z][a-zA-Z' -]{1,20}$/;
const MIDDLE_PATTERN = /^[A-Z]?[a-zA-Z' -]+$/;
const NAME_PATTERN_MSG = 'Names must start with a capital letter';

const schema = z.object({
  firstName: z
    .string({ required_error: 'First Name is required but is missing' })
    .min(2, 'First Name is required but is missing')
    .max(20)
    .regex(NAME_PATTERN, NAME_PATTERN_MSG),
  middleName: z.string().max(20).regex(MIDDLE_PATTERN, NAME_PATTERN_MSG).or(z.literal('')).optional(),
  lastName: z
    .string({ required_error: 'Last Name is required but is missing' })
    .min(2, 'Last Name is required but is missing')
    .max(20)
    .regex(NAME_PATTERN, NAME_PATTERN_MSG),
});

const DEFAULTS: NameValues = { firstName: '', middleName: '', lastName: '' };

const NameForm = ({ initialValues, onComplete, onBack, onCancelEdit, submitLabel }: TaskStepRenderArgs) => {
  const defaults = (initialValues as NameValues | undefined) ?? DEFAULTS;
  const {
    control,
    handleSubmit,
    formState: { isValid },
  } = useForm<NameValues>({
    defaultValues: defaults,
    resolver: zodResolver(schema),
    mode: 'onChange',
  });

  const onSubmit = handleSubmit((values) => {
    const cleaned: NameValues = {
      firstName: values.firstName,
      lastName: values.lastName,
      ...(values.middleName ? { middleName: values.middleName } : {}),
    };
    onComplete(cleaned);
  });

  return (
    <form className="rnq-form" onSubmit={(e) => e.preventDefault()}>
      <TextField control={control} name="firstName" label="First Name" required maxLength={20} />
      <TextField control={control} name="middleName" label="Middle Name" maxLength={20} />
      <TextField control={control} name="lastName" label="Last Name" required maxLength={20} />
      <NavRow
        onBack={onBack}
        onCancelEdit={onCancelEdit}
        onSubmit={onSubmit}
        submitLabel={submitLabel}
        disabled={!isValid}
      />
    </form>
  );
};

const Name: TaskDefinition = {
  key: 'task1',
  label: 'Name',
  title: 'To get started, please provide your name.',
  render: (args) => <NameForm {...args} />,
};

export default Name;
